import * as moment from "moment";

import { Appointment } from "@teamprevent/common/calendar";
import { MedicalTypeId } from "@teamprevent/common/healthSurveillance";
import { ClinicLocationTypeName } from "@teamprevent/common/appointment/models";

import { Link } from "@teamprevent/common/task/taskActions/models";
import { Clinic, NonSlotClinic } from "@teamprevent/common/calendar/models";

import { Room } from "../../crud/room";

export type ClinicDetails = Clinic;

export interface MobileClinicDetails extends NonSlotClinic {
    mobileUnit: string;
    address: string;
    rooms: undefined;
    clinicType: "Health Surveillance";
    customer: { id: string; name: string };
}

export type UnknownEmployeeAppointment = Appointment & {
    ohCaseId?: undefined;
    workGroups: string[];
}

export interface ClinicErrors {
    errors: { roomId?: string; userClashIds: string[] };
}

export interface EditClinicCliniciansErrors {
    errors: { userClashIds: string[] };
}

export interface ClinicType {
    name: string;
    usesSlots: boolean;
}

export enum BillabilityType {
    BillableByHour = "Billable by hour",
    BillableByTask = "Billable by task",
    BillableByHalfDay = "Billable by half day",
    BillableByDay = "Billable by day",
}

export const BillableByTypeOptions: BillableByType[]  = [
    { name: BillabilityType.BillableByHour },
    { name: BillabilityType.BillableByTask },
    { name: BillabilityType.BillableByHalfDay },
    { name: BillabilityType.BillableByDay },
];

export interface BillableByType {
    name: BillabilityType;
}

export interface ClinicLocationType {
    name: ClinicLocationTypeName;
    requiresRoom: boolean;
}

export interface CloneClinicRequest {
    clinicId: string;
    dates: {start: Date; end: Date}[];
    blockedTimeIds: string[];
}

export interface CloneClinicResult {
    date: Date;
    roomClashIds: string[];
    teamPreventUserClashIds: string[];
}

export interface EditClinicModel {
    start: moment.Moment;
    end: moment.Moment;
    billableByType: string;
    billableCustomerIds: string[];
    billableOrgStructure?: Link;
    clinicLocationTypes: ClinicLocationType[];
    roomId?: string;
}

export interface EditClinicResult {
    success: boolean;
    clinic: ClinicDetails;
}

export interface BlockClinicModel {
    notes: string;
    start: moment.Moment;
    end: moment.Moment;
    bookingTypeId?: string;
}

export interface BlockClinicResult {
    success: boolean;
    clinic: ClinicDetails;
}

export interface OptionValue {
    id: string;
    name: string;
}
export interface ClinicCreateModel {
    start: moment.Moment;
    end: moment.Moment;
    clinicType: ClinicType;
    clinicLocationTypes: ClinicLocationType[];
    room: Room;
    slotLengthInMinutes?: number;
    cliniciansRequired: number;
    teamPreventUsers: OptionValue[];
    customers: OptionValue[];
    billableByType: BillableByType;
    billableCustomers: OptionValue[];
    billableOrgStructure?: Link;
}

export interface MobileClinicCreateModel {
    mobileUnit: "";
    address: "";
    start: moment.Moment;
    end: moment.Moment;
    cliniciansRequired: number;
    teamPreventUsers: OptionValue[];
    customerId: string;
}

export interface AddUnknownEmployeeModel {
    workGroups: string[];
    caseTypes: string | null;
    employeeName: string | null;
    matchingEmployee?: string;
    title?: string;
    firstName?: string;
    lastName?: string;
    secondaryReference?: string;
    gender?: string;
    emailAddress?: string;
    jobTitle?: string;
    dateOfBirth?: moment.Moment;
    dateOfBirthStringified?: string;
    addressLine1?: string;
    postcode?: string;
}

export const emptyAddUnknownEmployeeModel = (): AddUnknownEmployeeModel => ({
    workGroups: [],
    caseTypes: null,
    employeeName: null,
});

export interface MatchingEmployeeModel {
    id: string;
    firstName: string;
    lastName: string;
    title: string;
    postCode: string;
    jobTitle: string;
    gender: string;
    dateOfBirth: moment.Moment;
    workGroups: string[];
    personalReferenceNumber: string;
}

export interface PendingAddUnknownEmployeeModel extends AddUnknownEmployeeModel {
    id: string;
    clinicId: string;
}

export interface AddUnknownEmployeeModelResult extends AddUnknownEmployeeModel {
    id: string;
    clinicId: string;
}

export interface ClinicState {
    clinics: ClinicDetails[];
    clinicErrors?: ClinicErrors;
}

export interface ClinicAndDistance {
    id: string;
    distance: number;
}

export interface ClinicAndDistanceFailedGeocodeResult {
    geocodedSuccessful: false;
}

export interface ClinicAndDistanceResult {
    geocodedSuccessful: true;
    clinicsAndDistances: ClinicAndDistance[];
}

export interface HealthSurveillanceCaseInfoModel {
    ohCaseId: string;
    caseId: number;
    healthSurveillanceCaseTypes: string[];
    dueDate?: moment.Moment;
    medicals: MedicalTypeId[];
}

export const isMobileClinic = (clinic: ClinicDetails): clinic is MobileClinicDetails => {
    const mobileClinic = clinic as MobileClinicDetails;
    return mobileClinic && mobileClinic.address !== undefined && mobileClinic.mobileUnit !== undefined;
};

export const billableCustomerBillingTypes = [BillabilityType.BillableByDay, BillabilityType.BillableByHalfDay, BillabilityType.BillableByHour];

export const shouldShowBillableCustomerField = (billableType?: BillableByType) => {
    if (!billableType) {
        return false;
    }

    return billableCustomerBillingTypes.includes(billableType.name);
};

export const ALL_CUSTOMERS = "All";

export const ALL_CUSTOMERS_VALUE: OptionValue = { id: ALL_CUSTOMERS, name: ALL_CUSTOMERS };

export const ALL_CUSTOMERS_OPTION = { key: ALL_CUSTOMERS, value: ALL_CUSTOMERS, text: ALL_CUSTOMERS };
